import React from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable } from '../../../../../shared/components/common/data-table/DataTable';
import { Column } from 'primereact/column';
import { Skeleton } from 'primereact/skeleton';
import { useGetDomainsStats } from '../../../../../shared/api/hooks/monitor/data/sources/UseGetDomainsStats';
import { DomainStats } from '../../../../../shared/types/monitor/data/sources/DomainStats';
import CustomLink from '../../../../../shared/components/custom-link/CustomLink';
import CustomTooltip from '../../../../../shared/components/common/tooltip/CustomTooltip';
import { ChatType } from '../../../../../shared/types/monitor/ChatType';
import EmptyPanel from '../../../../../shared/components/empty-panel/EmptyPanel';
import './TopDomainsTable.scss';

interface TopDomainsTableProps {
  projectId: number;
  chatType: ChatType | null;
  startDate: Date | null;
  endDate: Date | null;
  monitorPromptIds?: number[];
  filterMonitorTypes?: string[];
}

const TopDomainsTable: React.FC<TopDomainsTableProps> = ({
  projectId,
  chatType,
  startDate,
  endDate,
  monitorPromptIds,
  filterMonitorTypes,
}) => {
  const { t } = useTranslation();

  const { domainsStats, isLoading } = useGetDomainsStats(
    projectId,
    chatType,
    startDate,
    endDate,
    1,
    10,
    // `undefined` = no domain filter. MUST NOT be null: the hook's queryEnabled guard is
    // `domain !== null`, so passing null DISABLES the query (empty table).
    undefined,
    undefined,
    monitorPromptIds,
    filterMonitorTypes
  );

  return (
    <div className="stats__panel top-domains-table">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('topDomains')}
          <CustomTooltip tooltipText="topDomainsTooltip" addMargin={false} />
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="skeleton--table skeleton--table-4">
          <span />
          <span />
          <span />
          <span />
        </Skeleton>
      ) : (
        <DataTable
          value={domainsStats.domains}
          stripedRows
          tableClassName="table"
          className="table-wrapper table-wrapper--stack"
          responsiveLayout="stack"
          emptyMessage={() => <EmptyPanel />}
          dataKey="domain"
        >
          <Column
            header={t('position')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center table__cell--stack-rank"
            body={(_rowData: DomainStats, { rowIndex }: { rowIndex: number }) => `#${rowIndex + 1}`}
            style={{ width: '50px', minWidth: '50px' }}
          />
          <Column
            field="domain"
            bodyClassName="table__cell--stack-title"
            header={t('domain')}
            headerClassName="table__header"
            body={(rowData: DomainStats) => (
              <div className="source-data">
                <span
                  className="source-data__icon"
                  style={{ backgroundImage: `url("https://favicon.is/${rowData.domain}")` }}
                />
                <div className="source-data__details source-data__details--basic">
                  <span className="text-ellipsis">
                    <CustomLink url={rowData.domain} target="_blank" addHttps={true} />
                  </span>
                  <span className="top-domains-table__usages">
                    {t('usagesCount', { count: rowData.count })}
                  </span>
                </div>
              </div>
            )}
            style={{ minWidth: '120px' }}
          />
          <Column
            field="count"
            header={t('usages')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center top-domains-table__count"
            body={(rowData: DomainStats) => rowData.count}
            style={{ width: '80px', minWidth: '80px' }}
          />
        </DataTable>
      )}
    </div>
  );
};

export default TopDomainsTable;
